// Utility functions for PatientCardList and related components
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { GoogleDate } from 'utils/dateUtils';

export const COLOR = {
  blueBg: '#E3F2FD',
  white: '#FFFFFF',
  blueText: '#4078A6',
  grayText: '#D9D9D9',
  lightOrange: '#FFF3E0',
  orange: '#F57C00',
  darkOrange: '#BF360C',
  lightYellow: '#FFECB3',
  lightRed: '#FFCDD2',
  darkRed: '#B71C1C',
  lightPurple: '#EDE7F6',
  purple: '#7E57C2',
  lightGreen: '#E8F5E9',
  darkGreen: '#2E7D32',
  lightPink: '#FEEBEE',
};

// Utility to get difference in days between GoogleDate/Date/string/number and today
export function getDaysDifferenceFromToday(
  date: GoogleDate | Date | string | number,
): number | null {
  let targetDate: Date | null = null;

  if (date instanceof Date) {
    targetDate = date;
  }
  else if (typeof date === 'string' || typeof date === 'number') {
    targetDate = new Date(date);
  }
  else if (
    typeof date === 'object'
    && 'year' in date
    && 'month' in date
    && 'day' in date
  ) {
    targetDate = new Date(date.year, date.month - 1, date.day);
  }

  if (targetDate && !isNaN(targetDate.getTime())) {
    const now = new Date();
    const diffMs = now.getTime() - targetDate.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  return null;
}

export function formatDOB(dateOfBirth: PatientObj['dateOfBirth']): string {
  if (!dateOfBirth) return '';
  let d: Date | null = null;
  if (typeof dateOfBirth === 'string' || typeof dateOfBirth === 'number') {
    d = new Date(dateOfBirth);
  }
  else if (
    typeof dateOfBirth === 'object'
    && 'year' in dateOfBirth
    && 'month' in dateOfBirth
    && 'day' in dateOfBirth
  ) {
    d = new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day);
  }
  if (d && !isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  return '';
}

type FormatRelativeOptions = {
  prefix?: string;
};

export function formatRelativeDate(
  date: GoogleDate | Date | string | number | undefined,
  options?: FormatRelativeOptions,
): string | null {
  if (!date) return null;

  let targetDate: Date | null = null;
  if (date instanceof Date) {
    targetDate = date;
  }
  else if (typeof date === 'string' || typeof date === 'number') {
    targetDate = new Date(date);
  }
  else if (
    typeof date === 'object'
    && 'year' in date
    && 'month' in date
    && 'day' in date
  ) {
    targetDate = new Date(date.year, date.month - 1, date.day);
  }

  if (targetDate && !isNaN(targetDate.getTime())) {
    const diffDays = getDaysDifferenceFromToday(targetDate);
    const prefix = options?.prefix ?? '';
    if (diffDays === 0) return prefix ? `${prefix} today` : 'today';
    if (diffDays === 1) return prefix ? `${prefix} yesterday` : 'yesterday';
    if (diffDays !== null && diffDays < 7) return prefix ? `${prefix} ${diffDays} days ago` : `${diffDays} days ago`;
    if (diffDays !== null && diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return prefix
        ? `${prefix} ${weeks} week${weeks > 1 ? 's' : ''} ago`
        : `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    if (diffDays !== null && diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return prefix
        ? `${prefix} ${months} month${months > 1 ? 's' : ''} ago`
        : `${months} month${months > 1 ? 's' : ''} ago`;
    }
    if (diffDays !== null) {
      const years = Math.floor(diffDays / 365);
      return prefix
        ? `${prefix} ${years} year${years > 1 ? 's' : ''} ago`
        : `${years} year${years > 1 ? 's' : ''} ago`;
    }
  }

  return date?.toString() ?? null;
}

// Usage replacements:
export const formatActivity = (
  activity: GoogleDate | Date | string | number | undefined,

) => formatRelativeDate(activity, { prefix: 'active' });

export const formatLastUpdated = (
  dateAdded: GoogleDate | Date | string | number,
) => formatRelativeDate(dateAdded);
